'use client';
import * as React from 'react';
import type { CartItem } from '@/lib/types';

/** Product fields needed to add a line to the cart (quantity is managed here). */
export type CartProduct = Omit<CartItem, 'quantity'>;

interface CartContextValue {
  items: CartItem[];
  /** Total number of units across all lines. */
  count: number;
  /** Sum of price × quantity, in minor units. */
  totalCents: number;
  add: (product: CartProduct, quantity?: number) => void;
  setQuantity: (productId: string, quantity: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
}

const CartContext = React.createContext<CartContextValue | null>(null);

const STORAGE_KEY = 'av_cart';

function load(): CartItem[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as CartItem[]) : [];
    return Array.isArray(parsed) ? parsed.filter((i) => i.productId && i.quantity > 0) : [];
  } catch {
    return [];
  }
}

/**
 * Cart state shared across the site, persisted to localStorage. Optional: the
 * cart only appears when you render its components (Cart, AddToCartButton), so
 * mounting the provider site-wide is harmless for sites that don't use it.
 */
export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<CartItem[]>([]);

  // Hydrate from storage after mount to keep server/client markup identical.
  React.useEffect(() => setItems(load()), []);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = React.useCallback((product: CartProduct, quantity = 1) => {
    if (quantity <= 0) return;
    setItems((prev) => {
      const existing = prev.find((i) => i.productId === product.productId);
      if (existing) {
        return prev.map((i) =>
          i.productId === product.productId ? { ...i, quantity: i.quantity + quantity } : i,
        );
      }
      return [...prev, { ...product, quantity }];
    });
  }, []);

  const setQuantity = React.useCallback((productId: string, quantity: number) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.productId !== productId)
        : prev.map((i) => (i.productId === productId ? { ...i, quantity } : i)),
    );
  }, []);

  const remove = React.useCallback(
    (productId: string) => setItems((prev) => prev.filter((i) => i.productId !== productId)),
    [],
  );

  const clear = React.useCallback(() => setItems([]), []);

  const value = React.useMemo<CartContextValue>(() => {
    const count = items.reduce((n, i) => n + i.quantity, 0);
    const totalCents = items.reduce((n, i) => n + i.priceCents * i.quantity, 0);
    return { items, count, totalCents, add, setQuantity, remove, clear };
  }, [items, add, setQuantity, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/** Read and mutate the cart. Throws if used outside a CartProvider. */
export function useCart(): CartContextValue {
  const ctx = React.useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a <CartProvider>.');
  return ctx;
}
